import { ChevronDown, Info, LockKeyhole, Rows3 } from "lucide-react";
import type { CustomerEvidenceImport } from "@shared/customer-evidence";
import {
  getEvidenceSummary,
  getMappedFieldLabels,
  getUnmappedFieldLabels,
  type EvidenceTableGroup,
  type EvidenceTableRow,
} from "@/components/customer-evidence-summary-data";

function Metric({ label, value, detail }: { label: string; value: number | null; detail: string }) {
  return (
    <div className="rounded-xl border border-border/60 bg-background/75 px-4 py-3">
      <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{label}</p>
      <p className="mt-1 font-mono text-2xl font-semibold tabular-nums tracking-tight">{value === null ? "Not available" : value.toLocaleString()}</p>
      <p className="mt-1 text-[11px] leading-snug text-muted-foreground">{detail}</p>
    </div>
  );
}

function SignalList({
  title,
  descriptor,
  rows,
}: {
  title: string;
  descriptor: string;
  rows: { label: string; count: number; pct: number }[];
}) {
  if (!rows.length) {
    return (
      <section className="rounded-xl border border-dashed border-border/70 p-4">
        <h4 className="text-sm font-semibold">{title}</h4>
        <p className="mt-1 text-xs text-muted-foreground">Not available in this import.</p>
      </section>
    );
  }
  return (
    <section className="rounded-xl border border-border/60 bg-background/75 p-4">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h4 className="text-sm font-semibold">{title}</h4>
          <p className="mt-0.5 text-[11px] text-muted-foreground">{descriptor}</p>
        </div>
        <span className="rounded-full bg-muted px-2 py-1 text-[10px] font-medium text-muted-foreground">Observed</span>
      </div>
      <ul className="mt-3 space-y-2">
        {rows.map((row) => (
          <li key={row.label} className="grid grid-cols-[minmax(0,1fr)_auto] items-baseline gap-3 text-xs">
            <span className="min-w-0 break-words font-medium">{row.label}</span>
            <span className="font-mono tabular-nums text-muted-foreground">
              {row.count.toLocaleString()} <span className="ml-1">{row.pct}%</span>
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function DetailTable({ group }: { group: EvidenceTableGroup }) {
  return (
    <section className="overflow-hidden rounded-xl border border-border/60 bg-background">
      <div className="border-b border-border/50 bg-muted/25 px-4 py-3">
        <h4 className="text-sm font-semibold">{group.title}</h4>
        <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">{group.basis}</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-xs">
          <thead>
            <tr className="border-b border-border/40 text-[10px] uppercase tracking-wide text-muted-foreground">
              <th scope="col" className="px-4 py-2 font-medium">Observed category</th>
              <th scope="col" className="px-3 py-2 text-right font-medium">Count</th>
              <th scope="col" className="px-3 py-2 text-right font-medium">Share</th>
              <th scope="col" className="px-4 py-2 text-right font-medium">Base</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/35">
            {group.rows.map((row) => <TableRow key={`${group.key}-${row.label}`} row={row} />)}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function TableRow({ row }: { row: EvidenceTableRow }) {
  return (
    <tr>
      <th scope="row" className="min-w-44 px-4 py-2.5 text-left font-medium">
        <span>{row.label}</span>
        {row.note && <span className="mt-1 block max-w-xl whitespace-normal text-[11px] font-normal leading-relaxed text-muted-foreground">{row.note}</span>}
      </th>
      <td className="whitespace-nowrap px-3 py-2.5 text-right font-mono tabular-nums">{row.count === undefined ? "Not available" : row.count.toLocaleString()}</td>
      <td className="whitespace-nowrap px-3 py-2.5 text-right font-mono tabular-nums text-muted-foreground">{row.pct === undefined ? "Not available" : `${row.pct}%`}</td>
      <td className="whitespace-nowrap px-4 py-2.5 text-right font-mono tabular-nums text-muted-foreground">{row.denominator === undefined ? "Not available" : row.denominator.toLocaleString()}</td>
    </tr>
  );
}

export function CustomerEvidenceSummary({ item }: { item: CustomerEvidenceImport }) {
  const summary = getEvidenceSummary(item);
  const mapped = getMappedFieldLabels(item);
  const unmapped = getUnmappedFieldLabels(item);
  const { aggregate } = summary;
  return (
    <div className="space-y-4 pt-3">
      <section className="overflow-hidden rounded-2xl border border-primary/15 bg-primary/[0.035]">
        <div className="border-b border-primary/10 px-4 py-3 sm:px-5">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 rounded-lg bg-primary/10 p-2 text-primary"><Rows3 className="h-4 w-4" /></div>
            <div>
              <h3 className="text-sm font-semibold">Evidence at a glance</h3>
              <p className="mt-1 max-w-3xl text-xs leading-relaxed text-muted-foreground">
                A view of the aggregate that was observed in this import. Demographic patterns describe who appears in the data; they do not establish motivations, preferences, or a validated core buyer.
              </p>
            </div>
          </div>
        </div>
        <div className="grid gap-2 p-3 sm:grid-cols-3 sm:p-4">
          <Metric label="Source rows" value={aggregate.rows} detail="Rows in the source file" />
          <Metric label="Usable rows" value={aggregate.usable_rows} detail="Rows with usable audience evidence" />
          <Metric label="Companies" value={aggregate.company_fields_basis === "eligible_rows" ? null : aggregate.companies} detail={aggregate.company_fields_basis === "eligible_rows" ? "Unique company count unavailable" : "Deduplicated within this import"} />
        </div>
        <div className="grid gap-3 border-t border-primary/10 p-3 sm:grid-cols-3 sm:p-4">
          <SignalList title="Title groups" descriptor={summary.titleBasis} rows={summary.titleLeaders} />
          <SignalList title="Industry mix" descriptor={summary.companyBasis} rows={summary.industryLeaders} />
          <SignalList title="Organization size" descriptor={summary.companyBasis} rows={summary.sizeLeaders.map(({ label, count, pct }) => ({ label, count, pct }))} />
          <SignalList title="Management level" descriptor={summary.titleBasis} rows={summary.managementLeaders} />
          <SignalList title="Department" descriptor={summary.departmentBasis} rows={summary.departmentLeaders} />
          <SignalList title="Company state" descriptor={summary.companyBasis} rows={summary.stateLeaders} />
        </div>
        <div className="border-t border-primary/10 px-4 py-4 sm:px-5">
          <h4 className="text-sm font-semibold">Summary of observed patterns</h4>
          <ul className="mt-2 list-disc space-y-1.5 pl-4 text-xs leading-relaxed text-muted-foreground">
            {summary.observations.map((observation) => <li key={observation}>{observation}</li>)}
          </ul>
        </div>
      </section>

      <section className="grid gap-3 sm:grid-cols-2">
        <div className="rounded-xl border border-border/60 bg-muted/20 p-4">
          <p className="text-xs font-semibold">Read the denominators</p>
          <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">
            People, title-eligible people, and companies are separate bases. {summary.peopleBasis} {summary.titleBasis} {summary.companyBasis}
          </p>
        </div>
        <div className="rounded-xl border border-amber-700/15 bg-amber-700/[0.035] p-4">
          <div className="flex gap-2">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-amber-800 dark:text-amber-300" />
            <div>
              <p className="text-xs font-semibold">Directional evidence, not proof</p>
              <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">
                Use these distributions as descriptive context. Missing fields, source coverage, and aggregation limits can shape what is visible.
              </p>
            </div>
          </div>
        </div>
      </section>

      <details className="group rounded-xl border border-border/60 bg-background">
        <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3.5 [&::-webkit-details-marker]:hidden">
          <div>
            <span className="text-sm font-semibold">Inspect all aggregate data</span>
            <span className="ml-2 text-xs text-muted-foreground">{summary.details.length} sections, including missing values and optional fields</span>
          </div>
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180" />
        </summary>
        <div className="space-y-3 border-t border-border/50 p-3 sm:p-4">
          {aggregate.caveats.length > 0 && (
            <section className="rounded-xl border border-border/60 bg-muted/15 p-4">
              <h3 className="text-xs font-semibold">Import caveats</h3>
              <ul className="mt-2 list-disc space-y-1.5 pl-4 text-xs leading-relaxed text-muted-foreground">
                {aggregate.caveats.map((caveat, index) => <li key={`${index}-${caveat}`}>{caveat}</li>)}
              </ul>
            </section>
          )}
          <div className="grid gap-3 sm:grid-cols-2">
            <Metric label="Person denominator" value={aggregate.denominators.person} detail="Used for person-level fields and missing counts" />
            <Metric label="Title denominator" value={aggregate.denominators.title} detail="Used for title-group and seniority distributions" />
          </div>
          {aggregate.company_fields_basis === "eligible_rows" && (
            <div className="rounded-lg border border-amber-700/15 bg-amber-700/[0.035] px-3 py-2.5 text-xs leading-relaxed text-muted-foreground">
              Company-level fields fall back to {aggregate.denominators.company_fields?.toLocaleString() ?? "0"} eligible rows here; this is not a unique-company denominator. The company count remains {aggregate.companies.toLocaleString()}.
            </div>
          )}
          <div className="grid gap-3 xl:grid-cols-2">
            {summary.details.map((group) => <DetailTable key={group.key} group={group} />)}
          </div>
          <section className="rounded-xl border border-border/60 bg-muted/15 p-4">
            <h4 className="text-sm font-semibold">Field mapping</h4>
            <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">Metadata only. Source column values and sample cells are not shown.</p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Mapped fields ({mapped.length})</p>
                <p className="mt-1.5 text-xs leading-relaxed">{mapped.length ? mapped.join(", ") : "None"}</p>
              </div>
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Not mapped ({unmapped.length})</p>
                <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">{unmapped.length ? unmapped.join(", ") : "All supported fields mapped"}</p>
              </div>
            </div>
          </section>
          <div className="flex items-center gap-2 rounded-lg border border-emerald-700/15 bg-emerald-700/[0.035] px-3 py-2.5 text-xs text-muted-foreground">
            <LockKeyhole className="h-4 w-4 shrink-0 text-emerald-800 dark:text-emerald-300" />
            <p>Names, phones, emails and profile links are never stored.</p>
          </div>
        </div>
      </details>
    </div>
  );
}
